import Seo from '@comp/seo'
import { motion } from 'framer-motion'
import Layout from '@comp/layout'
import TrustBand from '@comp/home/trust-band'
import FinalCta from '@comp/home/final-cta'
import AboutStory from '@comp/company/about-story'
import OfficeGrid, { Office } from '@comp/company/office-grid'
import Container from '@comp/ui/container'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'

const EASE = [0.22, 1, 0.36, 1] as const

const offices: Office[] = [
  {
    city: 'New York',
    country: 'United States',
    address: '99 Wall Street #5534',
    isHq: true,
  },
  {
    city: 'Strasbourg',
    country: 'France',
    address: '14B Rue du Maréchal Lefebvre',
  },
  {
    city: 'Dubai',
    country: 'United Arab Emirates',
    address:
      'Area 271, Emirates Towers Boulevard, Trade Centre 2, Sheikh Zayed Road',
  },
  {
    city: 'Istanbul',
    country: 'Türkiye',
    address: 'Dijitalpark Teknokent PK: 34788 Çekmeköy',
  },
]

export default function CompanyPage() {
  return (
    <Layout>
      <Seo
        title="About Monofor: Identity & Access Management"
        description="Monofor is an Identity & Access Management company building Monosign, Monopam, and Monosync. Our mission: help every business reach a secure future."
        path="/company"
      />
      <main className="bg-surface text-ink-primary">
        <section className="relative overflow-hidden bg-surface pt-24 pb-20 lg:pt-32 lg:pb-24">
          <BackgroundField />
          <Container size="xl" className="relative">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
            >
              <Eyebrow tone="brand">About Monofor</Eyebrow>
            </motion.div>
            <motion.div
              className="mt-6 max-w-4xl"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: EASE, delay: 0.05 }}
            >
              <Heading as="h1" size="display-2xl">
                Securing identity
                <br />
                for the modern enterprise.
              </Heading>
            </motion.div>
            <motion.div
              className="mt-8 max-w-2xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: EASE, delay: 0.12 }}
            >
              <Text size="xl" tone="secondary">
                We help every business reach a secure future in information
                technologies, with IAM, PAM, and IGA built as one platform,
                not three.
              </Text>
            </motion.div>
          </Container>
        </section>

        <AboutStory />
        <OfficeGrid offices={offices} />
        <TrustBand />
        <FinalCta />
      </main>
    </Layout>
  )
}

function BackgroundField() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      <div className="absolute -top-32 -right-24 h-[640px] w-[640px] rounded-full bg-brand-100/70 blur-3xl" />
      <div className="absolute top-40 -left-32 h-[420px] w-[420px] rounded-full bg-accent-100/60 blur-3xl" />
      <svg
        className="absolute inset-0 h-full w-full text-brand-900/[0.04]"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern
            id="grid-company"
            width="56"
            height="56"
            patternUnits="userSpaceOnUse"
          >
            <path d="M.5 56V.5H56" fill="none" stroke="currentColor" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid-company)" />
      </svg>
    </div>
  )
}
